import React, {FC, useState, useCallback, useEffect} from 'react';
import {Button} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {Alpha3Code} from 'i18n-iso-countries';
import {useQuery, useApolloClient} from '@apollo/client';
import VacationViewToggle, {VacationView} from 'components/VacationViewToggle';
import PalowanVacationSearchBox from 'components/PalowanVacationSearchBox/PalowanVacationSearchBox';
import PalowanGroupedTable from 'viewComponents/PalowanGroupedTable';
import VacationDaysParamsTable from 'viewComponents/VacationDaysParamsTable';
import PalowanRangesModal from 'viewComponents/PalowanRangesModal';
import PalowanVacationCreateModal from 'viewComponents/PalowanVacationCreateModal';
import {GET_PALOWANS_WITH_VACATION_ASSIGNMENT_COUNT} from 'graphql/palowan-vacation/Queries';
import {
  PalowanWithAssignmentCount,
  PalowanWithAssignmentCountConnection,
} from 'models/PalowanVacationAssignment';
import {VacationRegulationProps} from 'models/configuration';

const DEFAULT_PAGE_SIZE = 10;

export interface VacationParamsSectionProps {
  /** Alpha3 country code from the management configuration (e.g., 'MEX', 'COL'). */
  country: Alpha3Code;
  /** Existing vacation regulations for the "Por País" view. */
  vacationRegulations?: VacationRegulationProps[];
  /** Callback to refresh parent data after a mutation. */
  fetch: () => Promise<void>;
  /** When false (country disabled), the "Nuevo parámetro" add button is disabled. */
  isActive?: boolean;
}

/**
 * Handles the Vacaciones tab content for a given country.
 * Renders a toggle that switches between the per-country regulation view
 * (existing VacationDaysParamsTable) and the per-palowan override view (new in US-02.01).
 *
 * The toggle is scoped to this tab — it does not affect other tabs or countries.
 * Uses Apollo useQuery directly so that search and pagination variable changes
 * automatically trigger re-fetches (GqlQueryController only reacts to internal state).
 *
 * @param country - Alpha3 code of the current country tab.
 * @param vacationRegulations - Regulation records for the "Por País" view.
 * @param fetch - Refresh callback from the parent query controller.
 * @returns The vacation parameters section with a view toggle.
 */
const VacationParamsSection: FC<VacationParamsSectionProps> = ({
  country,
  vacationRegulations,
  fetch,
  isActive,
}) => {
  const [selectedView, setSelectedView] = useState<VacationView>('country');
  // Palowan selected from the grouped table to open the ranges modal; null = modal closed.
  const [selectedPalowanForRanges, setSelectedPalowanForRanges] =
    useState<PalowanWithAssignmentCount | null>(null);
  // Controls visibility of the create modal for palowans without existing ranges.
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // Free-text search term wired to the PalowanVacationSearchBox.
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const client = useApolloClient();

  // useQuery fetches the grouped palowan list with aggregated range counts.
  // skip=true when viewing "Por País" to avoid unnecessary requests.
  const {data: queryData, loading: isLoading} = useQuery<{
    getPalowansWithVacationAssignmentCount: PalowanWithAssignmentCountConnection;
  }>(GET_PALOWANS_WITH_VACATION_ASSIGNMENT_COUNT, {
    variables: {
      countryCode: country,
      search: searchTerm || undefined,
      page,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    skip: selectedView !== 'palowan',
    fetchPolicy: 'cache-and-network',
  });

  const connection = queryData?.getPalowansWithVacationAssignmentCount ?? null;
  const rows: PalowanWithAssignmentCount[] =
    connection?.edges?.map(e => e.node) ?? [];
  const totalCount = connection?.totalCount ?? 0;

  // Guard: if the current page is beyond the last valid page (e.g. after filtering
  // results down to fewer records), reset to page 1 to avoid MUI out-of-range warnings.
  useEffect(() => {
    if (!isLoading && totalCount > 0) {
      const maxPage = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
      if (page > maxPage) {
        setPage(1);
      }
    }
  }, [totalCount, page, isLoading]);

  const handleViewChange = (view: VacationView) => {
    setSelectedView(view);
    // Reset pagination and search whenever the user switches views
    setPage(1);
    setSearchTerm('');
  };

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    // Reset to first page whenever the search changes
    setPage(1);
  }, []);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  /**
   * Handles palowan selection from the create picker.
   * Closes the picker dialog and opens PalowanRangesModal so the admin can
   * add the first range using the same inline table as the edit flow.
   *
   * @param palowan - The palowan selected from the search results.
   */
  const handlePalowanSelected = useCallback(
    (palowan: PalowanWithAssignmentCount) => {
      setIsCreateModalOpen(false);
      setSelectedPalowanForRanges(palowan);
    },
    []
  );

  const handleRangesChanged = useCallback(() => {
    // Refetch the grouped count table so totalRangeCount values stay fresh
    client.refetchQueries({
      include: ['GetPalowansWithVacationAssignmentCount'],
    });
  }, [client]);

  return (
    <div>
      {/* Top control row: toggle | search (grows). */}
      <div className="flex items-end gap-4 mb-4 flex-wrap">
        <VacationViewToggle
          selectedView={selectedView}
          onViewChange={handleViewChange}
        />
        {selectedView === 'palowan' && (
          <div className="flex-1">
            {/* Debounced free-text search for the grouped palowan table */}
            <PalowanVacationSearchBox
              onSearch={handleSearch}
              isLoading={isLoading}
            />
          </div>
        )}
        {selectedView === 'palowan' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={isActive === false}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Nuevo parámetro
          </Button>
        )}
      </div>
      {selectedView === 'country' ? (
        /* Existing per-country vacation regulation table */
        <VacationDaysParamsTable
          country={country}
          vacationRegulations={vacationRegulations}
          fetch={fetch}
        />
      ) : (
        /* Per-palowan grouped view — US-02.07 */
        <PalowanGroupedTable
          data={rows}
          loading={isLoading}
          page={page}
          pageSize={DEFAULT_PAGE_SIZE}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          onEdit={palowan => setSelectedPalowanForRanges(palowan)}
        />
      )}
      <PalowanRangesModal
        open={selectedPalowanForRanges !== null}
        palowan={selectedPalowanForRanges}
        isCountryActive={isActive !== false}
        onClose={() => setSelectedPalowanForRanges(null)}
        onRangesChanged={handleRangesChanged}
      />
      <PalowanVacationCreateModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        countryCode={country}
        onPalowanSelected={handlePalowanSelected}
      />
    </div>
  );
};

export default VacationParamsSection;
